import { graphql } from 'gatsby';
import { RichText } from 'prismic-reactjs';
import * as React from 'react';

import PrimaryLayout from '../layouts/PrimaryLayout';
import PageIntro from '../components/layout/PageIntro';
import htmlSerializer from '../components/layout/Prismic/PrismicRender';
import PlatformSubnav from '../components/layout/LayoutFrame/PlatformSubnav';
import { Typography, Divider } from '@nelnet/unifi-components-react';

interface IChangelogTemplateProps {
  data: {
    prismic: {
      allChangelogs: {
        edges: [
          {
            node: {
              title: string;
              version: string;
              changelogs: [
                {
                  release: string;
                  summary?: [];
                  library_updates?: [];
                  guidelines_updates?: [];
                  documentation_updates?: [];
                },
              ];
              _meta: {
                uid: string;
              };
            };
          },
        ];
      };
    };
  };
}

const ChangelogTemplate: React.FunctionComponent<IChangelogTemplateProps> = ({ data }) => {
  // Required check for no data being returned
  const doc = data.prismic.allChangelogs.edges.slice(0, 1).pop();
  if (!doc) {
    return null;
  }

  const { title, version, changelogs } = doc.node;
  const { uid } = doc.node._meta;
  const slug = `/changelog/${uid}/`;

  const pageTitle = title && RichText.asText(title);
  const logs = changelogs.reverse();

  return (
    <PrimaryLayout title={pageTitle || 'Prismic Preview'} path={slug}>
      <PlatformSubnav path={slug} />
      <PageIntro title={pageTitle || 'Prismic Preview'} path={slug} />
      <Typography variant="overline">Current version: {version}</Typography>
      <div>
        <Divider margin={{ top: 2 }} />
        {logs.map(
          ({ release, summary, library_updates, guidelines_updates, documentation_updates }) => {
            const content = [
              { title: 'Update Summary', content: summary },
              { title: 'Library', content: library_updates },
              { title: 'Content & Guidelines', content: guidelines_updates },
              { title: 'Documentation', content: documentation_updates },
            ];
            return (
              <article key={release}>
                <Typography variant="h2">{RichText.asText(release)}</Typography>
                {content.map(({ title, content }) => {
                  return content ? (
                    <>
                      <Typography component="h3">{title}</Typography>
                      <RichText render={content} htmlSerializer={htmlSerializer} />
                    </>
                  ) : null;
                })}
              </article>
            );
          },
        )}
      </div>
    </PrimaryLayout>
  );
};

export default ChangelogTemplate;

export const query = graphql`
  query ChangelogTemplateQuery($uid: String) {
    prismic {
      allChangelogs(uid: $uid) {
        edges {
          node {
            title
            version
            changelogs {
              release
              summary
              library_updates
              guidelines_updates
              documentation_updates
            }
            _meta {
              uid
            }
          }
        }
      }
    }
  }
`;
